import { useAppSelector } from "@/redux/hooks";
import { useCheckoutUI } from "../CheckoutUIProvider";
import { useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { fetchProductMetadataWithGraphQL } from "@/redux/reducers/checkout/thunk";

export const useCheckoutCartInfo = () => {
  const [state] = useCheckoutUI();
  const { checkoutIntent, checkoutIntentLoading, itemsToCheckout } =
    state.context;
  const userProfile = useAppSelector((state) => state.userProfile);
  const retailers = useAppSelector((state) => state.retailers);

  const [currentCart, setCurrentCart] = useState<{
    subtotal: number;
    estimatedShipping: number;
    estimatedTax: number;
    amountToCharge: number;
    items: {
      brandId: number;
      retailerSku: string;
      retailerParentSku: string;
      stacklineSku: string;
      quantity: number;
      retailPrice: number;
      retailerId: number;
    }[];
    deliveryEstimate: string;
    source: "checkoutIntent" | "cart";
    rewardAmounts: {
      retailerConnectRewardAmount: number;
      purchaseRewardAmount: number;
      totalRewardAmount: number;
    };
  }>({
    subtotal: 0,
    estimatedShipping: 0,
    estimatedTax: 0,
    amountToCharge: 0,
    items: [],
    deliveryEstimate: "",
    source: "checkoutIntent",
    rewardAmounts: {
      retailerConnectRewardAmount: 0,
      purchaseRewardAmount: 0,
      totalRewardAmount: 0,
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const apolloClient = useApolloClient();

  useEffect(() => {
    const fetchCheckoutItems = async () => {
      setIsLoading(true);
      if (checkoutIntent && userProfile?.userId && !checkoutIntentLoading) {
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === checkoutIntent.retailerId
        );
        const productsMetadata = await fetchProductMetadataWithGraphQL(
          apolloClient,
          checkoutIntent.items?.map((item) => item.stacklineSku) || [],
          !!userProfile?.userId
        );
        const purchaseRewardAmount = productsMetadata.reduce(
          (acc, item) => acc + item.totalRewardAmount,
          0
        );
        setCurrentCart({
          subtotal: checkoutIntent.estimatedOrderSummary?.subtotal || 0,
          estimatedShipping:
            checkoutIntent.estimatedOrderSummary?.shippingFees || 0,
          estimatedTax: checkoutIntent.estimatedOrderSummary?.tax || 0,
          amountToCharge: checkoutIntent.estimatedOrderSummary?.total || 0,
          items: checkoutIntent.items || [],
          deliveryEstimate:
            checkoutIntent.shippingOptions?.[0]?.deliveryEstimate || "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount,
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount + purchaseRewardAmount,
          },
          source: "checkoutIntent",
        });
      } else if (itemsToCheckout.length > 0) {
        const productsMetadata = await fetchProductMetadataWithGraphQL(
          apolloClient,
          itemsToCheckout.map((item) => item.stacklineSku),
          !!userProfile?.userId
        );
        const retailer = retailers.find(
          (retailer) => retailer.retailerId === itemsToCheckout[0].retailerId
        );
        const items = itemsToCheckout.map((item) => ({
          ...item,
          brandId:
            productsMetadata.find(
              (product) => product.stacklineSku === item.stacklineSku
            )?.brandId || 0,
          retailPrice:
            productsMetadata.find(
              (product) => product.stacklineSku === item.stacklineSku
            )?.retailPrice || 0,
          retailerParentSku:
            productsMetadata.find(
              (product) => product.stacklineSku === item.stacklineSku
            )?.retailerParentSku || "",
        }));
        setCurrentCart({
          items,
          subtotal: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          estimatedShipping: 0,
          estimatedTax: 0,
          amountToCharge: items.reduce(
            (acc, item) => acc + item.retailPrice * item.quantity,
            0
          ),
          deliveryEstimate: "",
          rewardAmounts: {
            retailerConnectRewardAmount:
              retailer?.retailerConnectRewardAmount || 0,
            purchaseRewardAmount: productsMetadata.reduce(
              (acc, item) => acc + item.totalRewardAmount,
              0
            ),
            totalRewardAmount:
              retailer?.retailerConnectRewardAmount +
              productsMetadata.reduce(
                (acc, item) => acc + item.totalRewardAmount,
                0
              ),
          },
          source: "checkoutIntent",
        });
      } else {
        setCurrentCart({
          items: [],
          subtotal: 0,
          estimatedShipping: 0,
          estimatedTax: 0,
          amountToCharge: 0,
          deliveryEstimate: "",
          source: "checkoutIntent",
          rewardAmounts: {
            retailerConnectRewardAmount: 0,
            purchaseRewardAmount: 0,
            totalRewardAmount: 0,
          },
        });
      }
      setIsLoading(false);
    };
    fetchCheckoutItems();
  }, [
    checkoutIntent,
    itemsToCheckout,
    apolloClient,
    checkoutIntentLoading,
    retailers,
    userProfile?.userId,
  ]);

  return {
    ...currentCart,
    isLoading,
  };
};
